/**
 * ADD-ACCOUNT.MODAL
 * Add a new account (wizard)
 * Step 1 : name, AE, consultant, number of employees
 * Step 2 : sector
 * Step 3 : modules and project
 */
import Modal from "@/components/modal"
import { connect } from "react-redux"
import { withTranslation, WithTranslation } from "react-i18next"
import { AccountState } from "@/redux/account.types"
import TextInput from "@/components/text-input"
import { store } from "@/index"
import {
  accountInsert_AsSupervisor,
  accountEdit,
  accountStatus,
  accountUpdate_AsSupervisor
} from "@/redux/account.actions"
import { STATUS_SAVED, STATUS_SAVE_ERROR, STATUS_SAVING } from "@/redux/_status.types"
import { NavigateFunction, useNavigate } from "react-router-dom"
import AccountCsTypeChips from "@/components/account-cs-type-chips"
import Checkbox from "@/components/checkbox"
import { useEffect, useState } from "react"
import { templateFetchAndGet } from "@/redux/template.actions"
import { TemplateState } from "@/redux/template.types"
import Link from "@/components/link"
import { projectCreateNew } from "@/redux/project.actions"
import Dropdown from "@/components/dropdown"
import Button from "@/components/button"
import Space from "@/components/space"
import ModulesWidget from "@/widgets/modules.widget"
import SectorGrid from "@/features/benchmark/entities/question/ui/modals/SectorGrid"
import { updateAccountSector_AsSupervisor } from "@/features/benchmark/entities/question/model/benchmarkQuestion.thunk"
import { supervisorFetch } from "@/redux/supervisor.actions"
import { Supervisor } from "@/redux/supervisor.types"
import { Session } from "@/redux/_session.types"
import { faArrowUpRightFromSquare, faChevronLeft } from "@fortawesome/free-solid-svg-icons"
import ListButton from "@/components/list-button"
import ModalConfirm from "@/components/modal-confirm"

interface StateProps extends WithTranslation {
  _session: Session
  account: AccountState
  template: TemplateState
}

interface OwnProps {
  onClose: Function
  onDone?: Function //Called when the account is created (instead of going to page /supervisor/account)
  hubspotUrl?: string //Link to the Hubspot record (from the deal)
}

type Props = StateProps & OwnProps

function AddAccountModal(props: Props) {
  const { t } = props
  const navigate: NavigateFunction = useNavigate()

  const [step, setStep] = useState(1)
  const [supervisors, setSupervisors] = useState<Supervisor[]>([])
  const [projectStep, setProjectStep] = useState<"QUESTION" | "TEMPLATE" | null>(null) //Last step : create a project ?
  const [selectedTemplateId, setSelectedTemplateId] = useState<null | string>(null)

  //Load templates and AE list (supervisors level 1 or 2)
  //AE is the connected supervisor if not already set
  //Survey feature and its main modules checked by default
  useEffect(() => {
    const { features, modules } = props.account.active
    store.dispatch(templateFetchAndGet(true))
    store.dispatch(accountEdit("features", { ...features, survey: true }))
    store.dispatch(
      accountEdit("modules", {
        ...modules,
        survey: { ...modules.survey, nps: true, rps: true, assistant: true, summarize: true, benchmark: true }
      })
    )

    async function loadSupervisors() {
      const response: any = await store.dispatch(supervisorFetch(""))
      if (response.error) return

      const list = response.map((x) => new Supervisor(x)).filter((x) => x.isSales)
      setSupervisors(list)

      const me = list.find((x) => x.id === props._session.supervisorId)
      if (!props.account.active.SupervisorIdSales && me) selectSales(me)
    }

    loadSupervisors()
  }, [])

  function selectSales(supervisor: Supervisor) {
    store.dispatch(accountEdit("SupervisorSales", supervisor))
    store.dispatch(accountEdit("SupervisorIdSales", supervisor.id))
  }

  //Save account (insert then update for the other fields), with a project or not
  //When done go to page /supervisor/account
  async function save(createProject: boolean) {
    const account = props.account.active

    store.dispatch(accountStatus(STATUS_SAVING))
    const response: any = await store.dispatch(
      accountInsert_AsSupervisor(account, createProject ? selectedTemplateId : null)
    )
    const responseUpdate: any = response.error ? response : await store.dispatch(accountUpdate_AsSupervisor(account))

    if (response.error || responseUpdate.error) {
      store.dispatch(accountStatus(STATUS_SAVE_ERROR))
    } else {
      if (account.options.sector) {
        await store.dispatch(updateAccountSector_AsSupervisor({ accountId: account.id, sector: account.options.sector }))
      }

      if (createProject) {
        await projectCreateNew(account.name, "Enquête " + account.name, response.template, account.id)
      }

      store.dispatch(accountStatus(STATUS_SAVED))
      if (props.onDone) props.onDone()
      else navigate("/supervisor/account")
    }
  }

  //Consulting account : created right after the first step (no sector, modules nor project)
  const isConsultant: boolean = props.account.active.isSuperAccount

  //Next step, then ask to create a project
  function next() {
    if (isConsultant) save(false)
    else if (step < 3) setStep(step + 1)
    else setProjectStep("QUESTION")
  }

  //Last step : ask to create a project
  if (projectStep === "QUESTION") {
    return (
      <ModalConfirm
        status={props.account.status}
        textBold="Souhaitez-vous créer un projet pour le compte ?"
        onNo={() => save(false)}
        onYes={() => setProjectStep("TEMPLATE")}
      />
    )
  }

  //Then choose the template of the project
  if (projectStep === "TEMPLATE") {
    return (
      <Modal
        disableClickOutside
        isCloseButtonVisible
        status={props.account.status}
        title="Quel template souhaitez-vous utiliser pour créer un projet ?"
        onClose={() => setProjectStep("QUESTION")}
        onNext={() => save(true)}
        nextDisabled={!selectedTemplateId}>
        <ul>
          {props.template.list
            .filter((x) => x.free)
            .map((template) => (
              <li key={template.id}>
                <Link
                  onClick={() => setSelectedTemplateId(template.id === selectedTemplateId ? null : template.id)}
                  isWithoutMargin>
                  <span className={template.id === selectedTemplateId ? "orange-t" : ""}>{template.label}</span>
                </Link>
              </li>
            ))}
        </ul>
      </Modal>
    )
  }

  return (
    <Modal
      disableClickOutside
      isLarge
      onClose={() => props.onClose()}
      title={
        <div className="flex items-center">
          {t("account_create")}
          <Space />
          {props.hubspotUrl && (
            <ListButton
              icon={faArrowUpRightFromSquare}
              text="Ouvrir la fiche Hubspot"
              onClick={() => window.open(props.hubspotUrl, "_blank")}
            />
          )}
        </div>
      }
      footer={
        <>
          {step > 1 && (
            <Button icon={faChevronLeft} onClick={() => setStep(step - 1)}>
              {t("utils_previous")}
            </Button>
          )}
          <Button onClick={() => props.onClose()}>{t("utils_close")}</Button>
          <Button
            className="primary"
            isLoading={props.account.status === STATUS_SAVING}
            onClick={next}>
            {isConsultant ? t("account_create") : t("utils_next")}
          </Button>
        </>
      }>
      {step === 1 && (
        <div>
          <div className="flex gap-4">
            <TextInput
              autoFocus
              onChange={(e) => store.dispatch(accountEdit("name", e.value))}
              title={t("account_name")}
              value={props.account.active.name}
            />

            <Dropdown
              active={props.account.active.SupervisorIdSales}
              displayField="name"
              list={supervisors}
              title={t("supervisor_sales")}
              onSelect={selectSales}
              value={props.account.active.SupervisorSales?.name}
            />

            <TextInput
              type="number"
              width={140}
              onChange={(e) => store.dispatch(accountEdit("invitations", e.value))}
              title={t("account_invitations")}
              value={props.account.active.invitations}
            />
          </div>

          <div>
            <div className="text-input-title flex">
              <span className="grey-t">Type de contrat</span>
            </div>
            <div className="my-1 flex h-[44px] items-center">
              <AccountCsTypeChips
                value={props.account.active.csType}
                onChange={(csType) => store.dispatch(accountEdit("csType", csType))}
              />
            </div>
          </div>

          <div className="mt-4">
            <Checkbox
              active={isConsultant}
              text={t("account_consulting")}
              onClick={() => store.dispatch(accountEdit("isSuperAccount", !isConsultant))}
            />
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          <SectorGrid
            current={props.account.active.options.sector}
            onSelect={(sector) =>
              store.dispatch(accountEdit("options", { ...props.account.active.options, sector }))
            }
          />
        </div>
      )}

      {step === 3 && (
        <div className="modal-content-list">
          <ModulesWidget features={props.account.active.features} modules={props.account.active.modules} />
        </div>
      )}

      <div className="height-20" />
    </Modal>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  account: state.account,
  template: state.template
})

export default connect(mapStateToProps)(withTranslation()(AddAccountModal))
